import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useQuery} from '@apollo/client';
import {Helmet} from 'react-helmet-async';

import Empty from '../empty/Empty';
import InfiniteScroll from '../infiniteScroll/InfiniteScroll';

import './CardDeck.css';
import SchemaOrgItemList from "../schemaOrg/SchemaOrgItemList";

const LIMIT = 40;

/**
 * Card deck component.
 */
export default function CardDeck({ children, itemsProperty, query, rowClassName, variables, limit = LIMIT, onlyNetwork = false, isEmptyMsg, schemaOrgPreview, keyword, noindexWhenEmpty = false, notFoundWhenEmpty = false }) {
    // fetch the items feed
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const { loading, error, data, fetchMore } = useQuery(
        query,
        {
            variables: {
                ...variables,
                offset: 0,
                limit: limit
            },
            fetchPolicy: onlyNetwork ? 'network-only' : 'cache-and-network', // force cache refresh to show new items
            onError: (error) => {
                console.error('Error loading the card deck', error);
            }
        }
    );

    useEffect(() => {
        if (data) {
            setHasMore(data[itemsProperty].length === offset + limit);
        }
    }, [data, itemsProperty, limit]);

    const loadMore = () => {
        const newOffset = data[itemsProperty].length;
        setOffset(newOffset);

        fetchMore({
            variables: {
                offset: newOffset
            },
            updateQuery: (previousResult, { fetchMoreResult }) => ({
                ...previousResult,
                [itemsProperty]: [...previousResult[itemsProperty], ...fetchMoreResult[itemsProperty]]
            })
        });
    };

    // reset the offset on query change
    useEffect(() => setOffset(0), [query, variables]);

    // 결과가 "확실히 0건"인지 판정한다.
    // 로딩 중(data 미도착)이나 쿼리 에러(백엔드 장애/SSR 프리페치 타임아웃으로 인한 빈 응답)를
    // 0건으로 오판하면 정상 페이지에 noindex/404가 붙어 색인이 통째로 빠지므로,
    // 세 조건을 모두 만족할 때만 참으로 본다.
    const items = data && data[itemsProperty];
    const isConfirmedEmpty = !loading && !error && Array.isArray(items) && items.length === 0;

    // build the UI elements
    const renderItem = (item, index) => (
        <li className="CardDeck-item col mb-4" key={item.id}>
            {children(item, index)}
        </li>
    );
    const listItems = data && data[itemsProperty] && data[itemsProperty].length ?
        data[itemsProperty].map(renderItem) :
        loading ?
        null :
        (
            <li className="CardDeck-empty">
                {isEmptyMsg && <Empty /> }
            </li>
        );

    return (
        <>
            {/* 검색 결과 0건 페이지가 색인되면 기생 SEO 스팸의 표적이 되므로 크롤러에서 제외한다 */}
            {noindexWhenEmpty && isConfirmedEmpty && (
                <Helmet>
                    <meta name="robots" content="noindex" />
                </Helmet>
            )}
            {/* 이미 색인된 스팸 URL의 제거를 앞당기기 위해 상태코드도 404로 내린다.
                (server/extractStatus.js 가 이 메타를 읽어 HTTP status 로 변환) */}
            {noindexWhenEmpty && notFoundWhenEmpty && isConfirmedEmpty && (
                <Helmet>
                    <meta httpEquiv="Status" content="404 Not Found" />
                </Helmet>
            )}
            <div className="CardDeck">
                <InfiniteScroll loading={loading} hasMore={hasMore} loadMore={loadMore}>
                    <ul className={`CardDeck-list list-unstyled row ${rowClassName}`}>
                        {listItems}
                    </ul>
                </InfiniteScroll>
            </div>
            {/* SSR 최초 렌더링 시에만 JSON-LD 출력 */}
            <SchemaOrgItemList
                offset={offset}
                items={data ? data[itemsProperty] : []}
                schemaOrgPreview={schemaOrgPreview}
                keyword={keyword}
            />
        </>
    );
}

CardDeck.propTypes = {
    children: PropTypes.func.isRequired,
    itemsProperty: PropTypes.string.isRequired,
    query: PropTypes.object.isRequired,
    rowClassName: PropTypes.string.isRequired,
    variables: PropTypes.object,
    limit: PropTypes.number,
    onlyNetwork: PropTypes.bool,
    isEmptyMsg: PropTypes.bool,
    schemaOrgPreview: PropTypes.object,
    keyword: PropTypes.string,
    noindexWhenEmpty: PropTypes.bool,
    notFoundWhenEmpty: PropTypes.bool,
};
